<template>
  <div class="wrapper event-row-wrapper" style="padding-bottom: 0.07rem">
    <div
      class="eventRow"
      :class="{
        eventOnly: !showActions,
      }"
    >
      <div
        style="
          height: 100%;
          border: 1px solid var(--p-gray-900);
          border-radius: 3px;
          position: relative;
        "
        :class="row2n % 2 ? 'row2n' : 'row2np1'"
        class="event border-noround-right"
      >
        <component
          :is="componentForEvent(event)"
          ref="eventWrapper"
          :event="newEventWithOriginalBoardId ?? event"
          :event-types="eventTypes"
          :style="`position: sticky; top: -${translationSize}px; height: unset;`"
          @removed-from-board="emit('removedFromBoard', $event)"
        />
      </div>
      <template v-if="showActions">
        <ActionListNew
          v-if="newEventWithOriginalBoardId != null"
          :event="newEventWithOriginalBoardId"
          :actions="newEventWithOriginalBoardId.ActionEventConnection.map((con) => con.action)"
          :row2n="row2n"
          :action-types="actionTypes"
        />
        <ActionList
          v-else
          :event="event"
          :actions="event.actions"
          :row2n="row2n"
          @open:ih-order-modal="openIHOrderModal"
        />
      </template>
    </div>
  </div>
</template>

<script setup lang="ts">
import { type PropType, provide, computed, inject, ref } from "vue";
import {
  SQDCBoardEventStatus,
  SQDCBoardIssueConnectionTypes,
  type EventListEventFields_SQDCEventOldFormatNewtuff_Fragment,
  type EventListEventFieldsFragment,
} from "@/graphql";
import IHFailureNoticeEvent from "@/modules/ih/components/IHFailureNoticeEvent.vue";
import IHMaintenanceEvent from "@/modules/ih/components/IHMaintenanceEvent.vue";
import ActionList from "../action/ActionList.vue";
import AuditResult from "../auditResult/AuditResult.vue";
import CommonIssue from "../issue/CommonIssue.vue";
import QualityAssurance from "../quality/QualityAssurance.vue";
import { BadgeColorInjectionKey } from "../issue/IssueTypes";
import IdeaEvent from "@/modules/idea/components/eab/IdeaEvent.vue";
import {
  BoardIdInjectionKey,
  EABEventInjectionKey,
} from "@/views/event-action/EventActionBoardInjectionKeys";
import ErrorRow from "./ErrorRow.vue";
import type {
  EABActionTypeType,
  EABEventTypeType,
} from "@/modules/sqdc/components/eventActionBoard/EventActionHelper";
import EventComponent from "@/modules/sqdc/components/eventActionBoard/EventComponent.vue";
import ActionListNew from "@/modules/sqdc/components/eventActionBoard/ActionList.vue";

const props = defineProps({
  event: {
    type: Object as PropType<EventListEventFieldsFragment>,
    required: true,
  },
  /** defines the starting background color (striped) of the action list */
  row2n: {
    type: Number,
    required: true,
  },
  translationSize: {
    type: Number,
    required: true,
  },
  eventTypes: {
    type: Array as PropType<EABEventTypeType[]>,
    required: false,
    default: () => [],
  },
  actionTypes: {
    type: Array as PropType<EABActionTypeType[]>,
    required: false,
    default: () => [],
  },
  showActions: {
    type: Boolean,
    default: true,
  },
});
const emit = defineEmits<{
  removedFromBoard: [eventId: number];
}>();
provide(BadgeColorInjectionKey, "");
provide(
  EABEventInjectionKey,
  computed(() => props.event),
);
const parentBoardId = inject(BoardIdInjectionKey, ref<number | null>(null));
provide(
  BoardIdInjectionKey,
  computed(
    () =>
      props.event.boardConnection.find(
        (connection) => connection.status === SQDCBoardIssueConnectionTypes.created,
      )?.boardId ??
      parentBoardId.value ??
      null,
  ),
);
const eventWrapper = ref<
  | InstanceType<typeof CommonIssue>
  | InstanceType<typeof AuditResult>
  | InstanceType<typeof IHFailureNoticeEvent>
  | InstanceType<typeof IHMaintenanceEvent>
  | InstanceType<typeof IdeaEvent>
  | InstanceType<typeof QualityAssurance>
  | null
>(null);
function componentForEvent(event: EventListEventFieldsFragment) {
  switch (event.__typename) {
    case "Issue":
      return CommonIssue;
    case "AuditResult":
      return AuditResult;
    case "IHFailureNotice":
      return IHFailureNoticeEvent;
    case "IHMaintenance":
      return IHMaintenanceEvent;
    case "HLPIdea":
      return IdeaEvent;
    case "QualityAssurance":
      return QualityAssurance;
    case "SQDCEventOldFormatNewtuff":
      return EventComponent;
    default:
      return ErrorRow;
  }
}

const newEvent = computed(() => {
  if (props.event.__typename === "SQDCEventOldFormatNewtuff") {
    return props.event as EventListEventFields_SQDCEventOldFormatNewtuff_Fragment;
  }
  return undefined;
});

const newEventWithOriginalBoardId = computed(() => {
  if (newEvent.value == null) {
    return undefined;
  }
  return {
    ...newEvent.value.newFormat,
    originalBoardId: newEvent.value.newFormat.BoardEventConnection.find(
      (el) => el.status === SQDCBoardEventStatus.CREATED,
    )?.boardId,
  };
});

// TODO: kill it (This should not be in here)
function openIHOrderModal() {
  if (eventWrapper.value) {
    /**
     * This condition is because the "Common Issue" is the only Event that utilizes <AbstractIssue> as entry Component.
     * All other Events are using <EventComponent> as entry.
     */
    if ("abstractRef" in eventWrapper.value) {
      eventWrapper.value.abstractRef?.eventRef?.eventActionButtonList?.openIHOrderModal?.();
    }
    if ("eventRef" in eventWrapper.value) {
      eventWrapper.value.eventRef?.eventActionButtonList?.openIHOrderModal?.();
    }
  }
}
</script>

<style lang="scss">
.event-row-wrapper {
  container-type: inline-size;
}

.eventRow {
  display: grid;
  grid-template-columns: 50% 50%;
  margin-bottom: 0.125em;

  &.eventOnly {
    grid-template-columns: 100%;

    .event {
      border-radius: 3px !important;
    }
  }

  .event {
    border-bottom-right-radius: 0;
    border-top-right-radius: 0;
  }

  .action-list {
    border-bottom-left-radius: 0 !important;
    border-top-left-radius: 0 !important;
  }

  .padding-bottom {
    padding-bottom: 50px;
  }
}
</style>
